import { Injectable } from '@angular/core';
import { HttpClient as Http, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { timeout } from 'rxjs/operators';

import { AppConfig } from 'src/shared/providers/config';
import { AppState } from 'src/shared/providers/http/app-state';
import { DeviceInfoProvider } from 'src/shared/providers/device-info';
import { Events } from 'src/providers/events/events';
import { Logger } from 'src/shared/providers/logger';
import { RequestCache } from 'src/shared/providers/http/request-cache';

import { RequestModel } from 'src/shared/models/request.model';
import { isOnPublicRoute } from 'src/shared/utils/public-routes';

import { DeviceInfo } from '@capacitor/device';

@Injectable()
export class HttpClient {
  constructor(
    public appState: AppState,
    public cache: RequestCache,
    public deviceInfo: DeviceInfoProvider,
    public events: Events,
    public http: Http,
    public logger: Logger
  ) {
  }

  async get(url): Promise<any> {
    if (!await this.appState.isOnline()) {
      return this.getFromCache(url);
    }

    const result = await this.request({method: 'GET', url});
    await this.cache.store(url, result);
    return result;
  }

  async getFromCache(url) {
    const storedData = await this.cache.retrieve(url);

    if (!storedData) {
      // TODO LOGGER
      // this.logger.log({ type: 'error', message: 'Data not available offline.', url });
      throw new Error('Something went wrong.');
    }

    return storedData;
  }

  // Skips the cache only while online: offline there is no fresh copy to get,
  // so the last stored one beats a request that can only fail.
  async getNoCache(url) {
    if (!await this.appState.isOnline()) {
      return this.getFromCache(url);
    }

    const result = await this.request({method: 'GET', url});
    await this.cache.store(url, result);
    return result;
  }

  post = (url, payload) => {
    return this.request({ method: 'POST', url, payload })
  };

  put = (url, payload) => {
    return this.request({ method: 'PUT', url, payload })
  };

  delete = (url) => {
    return this.request({method: 'DELETE', url})
  };

  async getWithParams(url, params) {
    const isExternalUrl = this.isExternalUrl(url);
    const formattedUrl = isExternalUrl ? url : `${AppConfig.API}${url}`;
    return this.http.get(formattedUrl, { headers: await this.header(isExternalUrl), params }).toPromise();
  }

  async request(request: RequestModel): Promise<any> {
    const isExternalUrl = this.isExternalUrl(request.url);
    const formattedUrl = isExternalUrl ? request.url : `${AppConfig.API}${request.url}`
    const startTime = Date.now();
    return this.http
      .request(request.method, formattedUrl, await this.payload(request.payload, request.method, isExternalUrl))
      .pipe(
        timeout(request.timeout || AppConfig.TIMEOUT_THRESHOLD)
      ).toPromise()
      .catch( error => this.onError(error, formattedUrl, request.payload) )
      .finally(() => {
        const endTime = Date.now();
        this.logDuration(startTime, endTime, formattedUrl, request?.payload);
      });
  }

  async fullRequest(request: RequestModel): Promise<any> {
    const isExternalUrl = this.isExternalUrl(request.url);
    const formattedUrl = isExternalUrl ? request.url : `${AppConfig.API}${request.url}`
    const startTime = Date.now();
    return this.http
      .request(request.method, formattedUrl, {
        ...await this.payload(request.payload, request.method, isExternalUrl),
        observe: 'response'
      })
      .pipe(
        timeout(request.timeout || AppConfig.TIMEOUT_THRESHOLD)
      ).toPromise()
      .catch( error => this.onError(error, formattedUrl, request.payload) )
      .finally(() => {
        const endTime = Date.now();
        this.logDuration(startTime, endTime, formattedUrl, request?.payload);
      });
  }

  async header(isExternalUrl: boolean): Promise<HttpHeaders> {
    const headers = {
      Authorization: `${localStorage.getItem('hk_tidy_token_type')} ${localStorage.getItem('hk_tidy_token')}`,
      'Content-Type': 'application/json'
    };

    if (!isExternalUrl) {
      const deviceInfo: DeviceInfo = await this.deviceInfo.getDeviceInfo();
      const deviceUUID: string = await this.deviceInfo.uuid();
      const appVersion: string = await this.deviceInfo.getAppVersion();

      headers['X-App-Version'] = AppConfig.VERSION;
      headers['X-App-Name'] = 'business-manager';
      headers['X-App-Device-UUID'] = deviceUUID;
      headers['X-App-Build'] = appVersion || AppConfig.VERSION;
      headers['X-App-Device-OS-Name'] = deviceInfo.operatingSystem;
      headers['X-App-Device-OS-Version'] = deviceInfo.osVersion;
      headers['X-App-Device-Model'] = deviceInfo.model;
    }
    const httpHeaders = new HttpHeaders(headers);
    return httpHeaders;
  }

  async payload(data: any, method: string, isExternalUrl: boolean): Promise<any> {
    return {
      headers: await this.header(isExternalUrl),
      body: JSON.stringify(data),
      method
    };
  }

  async onError(error, url, data): Promise<any> {
    let parsedError = error;
    if(error instanceof HttpErrorResponse) {
      parsedError = error?.error;
    }

    if (parsedError && error instanceof HttpErrorResponse) {
      const apiError = parsedError;
      parsedError = apiError.error ? apiError.error : apiError;
    }

    if (error instanceof HttpErrorResponse && this.isUnauthorized(parsedError) && !isOnPublicRoute()) {
      // A single transient 401 (a token-propagation race on app open, a server
      // hiccup, or an endpoint-specific auth failure) must NOT tear down the
      // whole session and bounce the user to the homepage - the intermittent
      // "app reloads and sends us home on login" bug (Trello 18582). Confirm the
      // token is genuinely dead by revalidating it once against the
      // authoritative `me` endpoint before firing auth:logout.
      this.confirmSessionInvalidAndLogout();
    }

    this.logger.requestError(parsedError, url, data);
    return Promise.reject(parsedError);
  }

  // De-duplicates concurrent revalidations: the request burst on app open can
  // produce several 401s at once, but only one `me` re-check should run.
  private sessionRevalidation: Promise<boolean> | null = null;

  // Fire-and-forget. Publishes `auth:logout` only when a fresh call to the
  // authoritative session endpoint also comes back unauthorized. The original
  // request still rejects immediately (its caller handles the error); the
  // session is only torn down if the token is actually invalid.
  private confirmSessionInvalidAndLogout(): void {
    if (!this.sessionRevalidation) {
      this.sessionRevalidation = this.isSessionStillValid()
        .finally(() => { this.sessionRevalidation = null; });
    }
    this.sessionRevalidation.then(isValid => {
      if (!isValid) {
        this.events.publish('auth:logout');
      }
    });
  }

  // A freshly issued token can be briefly rejected while the backend finishes
  // provisioning the session (the "log in -> white screen -> back to signup"
  // window in the QA recording). Re-check a few times, backing off, before
  // concluding the session is dead - a single propagation-window 401 must not
  // tear the session down.
  private readonly sessionRevalidationDelaysMs = [0, 1000, 2000];

  // True when the current token is still accepted by the API. A non-401 failure
  // (offline, timeout, 5xx) is treated as "still valid" so a network blip never
  // logs the user out, and only a 401 that persists across every retry counts
  // as a dead session. This call bypasses `onError`, so it can never recurse
  // into another logout attempt.
  private async isSessionStillValid(): Promise<boolean> {
    const url = `${AppConfig.API}api/v2/homekeeper/me`;
    for (const delayMs of this.sessionRevalidationDelaysMs) {
      if (delayMs > 0) {
        await this.delay(delayMs);
      }
      try {
        await this.http
          .get(url, { headers: await this.header(false) })
          .pipe(timeout(AppConfig.TIMEOUT_THRESHOLD))
          .toPromise();
        return true;
      } catch (error) {
        let parsedError: any = error;
        if (error instanceof HttpErrorResponse) {
          parsedError = error?.error;
          parsedError = parsedError?.error ? parsedError.error : parsedError;
        }
        const isAuthFailure = error instanceof HttpErrorResponse && this.isUnauthorized(parsedError);
        if (!isAuthFailure) {
          // Network / timeout / 5xx: not an auth problem, keep the session.
          return true;
        }
        // A 401 - fall through and retry after the next backoff.
      }
    }
    return false;
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  isExternalUrl(url): boolean {
    return (url.indexOf('https://') === 0);
  }

  isUnauthorized(error): boolean {
    if (!error) return false;
    const { code, message } = error;
    // NOTE: a "requires scope" message is deliberately NOT treated as a dead
    // session. It means the token is valid but lacks scope for one endpoint, so
    // logging the whole session out on it wrongly bounced users to the homepage
    // (Trello 18582). Only genuine token-invalid signals count here.
    return code == 401
      || message === 'Authentication failled for this homekeeper'
      || message === 'Authentication failed for this homekeeper'
      || message === 'The access token is invalid';
  }

  isTimeout(error) {
    if (!error) return false;
    const { message } = error;
    return message?.indexOf('Timeout') !== -1;
  }

  logDuration(startTime, endTime, url, payload) {
    const duration = endTime - startTime;
    if (duration >= AppConfig.REQUEST_LOG_THRESHOLD) {
      this.logger.log({
        message: 'Request duration',
        duration,
        url,
        payload
      })
    }
  }
}
